import { useCallback, useState } from "react";
import { useLocaleStore } from "stores/localeStore";
import { getUserInfoFromCache } from "utility/helper";

const BASE_URL = process.env.REACT_APP_OPEN_API_URL ?? process.env.REACT_APP_API_URL ?? "";

export type ProductTypeTranslationDto = {
  id?: number;
  name: string;
  languageId: number;
  languageName?: string | null;
  languageLocale?: string | null;
};

export type ProductTypeCoverageDto = {
  totalCount: number;
  withTypeCount: number;
  missingTypeCount: number;
};

export type ProductTypeDto = {
  id: number;
  level: number;
  sortOrder: number;
  isActive: boolean;
  parentProductTypeId?: number | null;
  name: string;
  productCount: number;
  translations: ProductTypeTranslationDto[];
  createdAt?: string;
  modifiedAt?: string;
};

export type ProductTypeTranslationRequest = {
  languageId: number;
  name: string;
};

export type ProductTypeCreateRequest = {
  parentProductTypeId?: number | null;
  translations: ProductTypeTranslationRequest[];
};

export type ProductTypeUpdateRequest = {
  parentProductTypeId?: number | null;
  isActive?: boolean | null;
  translations: ProductTypeTranslationRequest[];
};

export type ProductTypeMoveRequest = {
  parentProductTypeId?: number | null;
  index: number;
};

async function request<T>(
  path: string,
  options: RequestInit & { languageLocale?: string } = {}
): Promise<T> {
  const userInfo = getUserInfoFromCache();
  const { languageLocale, ...init } = options;
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${userInfo?.token ?? ""}`,
      ...(languageLocale ? { lang: languageLocale } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed (${response.status})`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

const useProductTypeApi = () => {
  const [loading, setLoading] = useState(false);
  const { activeLanguage } = useLocaleStore();

  const getProductTypeCoverage = useCallback(async (): Promise<ProductTypeCoverageDto> => {
    return await request<ProductTypeCoverageDto>("/api/product-types/coverage");
  }, []);

  const getProductTypes = useCallback(async (): Promise<ProductTypeDto[]> => {
    setLoading(true);
    try {
      return await request<ProductTypeDto[]>("/api/product-types", {
        languageLocale: activeLanguage?.locale,
      });
    } finally {
      setLoading(false);
    }
  }, [activeLanguage?.locale]);

  const getProductType = useCallback(
    async (id: number): Promise<ProductTypeDto | undefined> => {
      setLoading(true);
      try {
        return await request<ProductTypeDto>(`/api/product-types/${id}`, {
          languageLocale: activeLanguage?.locale,
        });
      } finally {
        setLoading(false);
      }
    },
    [activeLanguage?.locale]
  );

  const createProductType = useCallback(
    async (body: ProductTypeCreateRequest): Promise<ProductTypeDto> => {
      setLoading(true);
      try {
        return await request<ProductTypeDto>("/api/product-types", {
          method: "POST",
          body: JSON.stringify(body),
          languageLocale: activeLanguage?.locale,
        });
      } finally {
        setLoading(false);
      }
    },
    [activeLanguage?.locale]
  );

  const updateProductType = useCallback(
    async (id: number, body: ProductTypeUpdateRequest): Promise<ProductTypeDto> => {
      setLoading(true);
      try {
        return await request<ProductTypeDto>(`/api/product-types/${id}`, {
          method: "PUT",
          body: JSON.stringify(body),
          languageLocale: activeLanguage?.locale,
        });
      } finally {
        setLoading(false);
      }
    },
    [activeLanguage?.locale]
  );

  const moveProductType = useCallback(
    async (id: number, body: ProductTypeMoveRequest): Promise<ProductTypeDto> => {
      setLoading(true);
      try {
        return await request<ProductTypeDto>(`/api/product-types/${id}/move`, {
          method: "POST",
          body: JSON.stringify(body),
          languageLocale: activeLanguage?.locale,
        });
      } finally {
        setLoading(false);
      }
    },
    [activeLanguage?.locale]
  );

  const setProductTypeActive = useCallback(
    async (id: number, isActive: boolean): Promise<ProductTypeDto> => {
      setLoading(true);
      try {
        return await request<ProductTypeDto>(`/api/product-types/${id}/active`, {
          method: "PATCH",
          body: JSON.stringify({ isActive }),
          languageLocale: activeLanguage?.locale,
        });
      } finally {
        setLoading(false);
      }
    },
    [activeLanguage?.locale]
  );

  const deleteProductType = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    try {
      await request<void>(`/api/product-types/${id}`, {
        method: "DELETE",
        languageLocale: activeLanguage?.locale,
      });
    } finally {
      setLoading(false);
    }
  }, [activeLanguage?.locale]);

  return {
    loading,
    getProductTypes,
    getProductTypeCoverage,
    getProductType,
    createProductType,
    updateProductType,
    moveProductType,
    setProductTypeActive,
    deleteProductType,
  };
};

export default useProductTypeApi;
